import {ButtonWithIcon, LabelWithoutJson, Modal} from '@/components/atoms'
import {AccordionItemCustom as AccordionItem} from '@/components/molecules/AccordionItemCustom'
import {usePromotionConfig} from '@/hooks/usePromotionConfig'
import {PromotionStep} from '@/types/promotion'
import {COLOR, COLOR_CODE} from '@/utils/constants/colors'
import {FONT_SIZE, FONT_WEIGHT, SIZE} from '@/utils/constants/globalStyle'
import {displayOnMapping} from '@/utils/constants/promotionConstants'
import OpenInFullIcon from '@mui/icons-material/OpenInFull'
import {useState} from 'react'

const PromotionsReviewDisplay = ({promotionReviewData, isDetailView, handleEditStep, languages}) => {
  const {promotionConfig} = usePromotionConfig()
  const [showImagePreview, setShowImagePreview] = useState(false)

  const LabelWithVal = ({keyName, val}) => {
    const id = keyName?.toLowerCase()?.replaceAll(' ', '_') ?? ''
    return (
      <div className="col-4 overflow-hidden pt-5">
        <div>
          <LabelWithoutJson
            id={`${id}_label`}
            text={keyName}
            fontWeight={FONT_WEIGHT.NORMAL}
            fontSize={FONT_SIZE.XS}
            className="font-body"
          />
          <LabelWithoutJson
            id={`${id}_value`}
            text={val}
            isValueDiasbledInput={false}
            fontWeight={FONT_WEIGHT.BOLD}
            fontSize={FONT_SIZE.SM}
            className="font-body"
            color={COLOR_CODE._FFFFFF}
          />
        </div>
      </div>
    )
  }

  const getDisplayOnText = () => {
    return promotionReviewData.displayOn?.map(item => displayOnMapping[item] || item).join(', ') || '-'
  }

  const handleImageClick = () => {
    setShowImagePreview(true)
  }

  const handleClosePreview = () => {
    setShowImagePreview(false)
  }

  return (
    <>
      <AccordionItem
        isDark
        size="large"
        className="min-h-12 w-full gap-2 rounded p-4"
        labelClassName="font-body text-base font-medium leading-normal"
        label={
          <div className="flex w-full items-center justify-between">
            <div className="font-medium">Display</div>
          </div>
        }
        rightAccessoryChild={
          !isDetailView ? (
            <ButtonWithIcon
              iconLeading="edit"
              handleButton={() => handleEditStep(PromotionStep.DISPLAY)}
              uiSchema={{
                className: 'flex items-center text-sm text-[#0069D7] border-none ml-4',
                title: 'Edit',
                size: SIZE.SMALL,
                variant: COLOR.TERTIARY,
                isDark: true,
              }}
            />
          ) : null
        }
        presentation="test"
        content={
          <div
            className="promotions-content-wrapper w-full overflow-hidden"
            style={{padding: '12px 16px', gap: '16px', minWidth: '1238px'}}
          >
            {/* Display Image and Info */}
            <div className="w-full overflow-hidden">
              <div className="grid w-full grid-cols-1 gap-6 overflow-hidden pb-5">
                <div className="flex grid w-full grid-cols-3 items-start gap-6 overflow-hidden">
                  <div
                    className="relative cursor-pointer overflow-hidden rounded-lg border border-dotted border-[#818181]"
                    style={{width: '156px', height: '100px'}}
                    onClick={handleImageClick}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        handleImageClick()
                      }
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    {promotionReviewData.bannerUrl ? (
                      <>
                        <img
                          src={promotionReviewData.bannerUrl}
                          alt="Product Promotion"
                          className="h-full w-full object-cover"
                        />
                        {/* Expand icon overlay */}
                        <div className="absolute bottom-2 right-2 flex h-8 w-8 items-center justify-center rounded bg-black bg-opacity-80 p-1">
                          <OpenInFullIcon
                            style={{
                              fontSize: '16px',
                              color: 'white',
                            }}
                          />
                        </div>
                      </>
                    ) : (
                      <div className="h-full w-full bg-gray-100" />
                    )}
                  </div>
                  <div className="overflow-hidden">
                    <LabelWithVal keyName="Promotion Shows On" val={getDisplayOnText()} />
                  </div>
                  <div className="overflow-hidden">
                    <LabelWithVal keyName="Display Order" val={promotionReviewData.displayOrder?.toString() || '-'} />
                  </div>
                </div>
                <div className="flex grid w-full grid-cols-3 items-start gap-6 overflow-hidden">
                  <div className="overflow-hidden">
                    <LabelWithVal
                      keyName="Banner Type"
                      val={
                        promotionReviewData.bannerType
                          ? promotionReviewData.bannerType.charAt(0).toUpperCase() +
                            promotionReviewData.bannerType.slice(1)
                          : '-'
                      }
                    />
                  </div>
                  <div className="overflow-hidden">
                    <LabelWithVal
                      keyName="Landing Page"
                      val={
                        promotionReviewData.landingPage
                          ? promotionConfig?.landingPage?.find(page => page.value === promotionReviewData.landingPage)
                              ?.label || promotionReviewData.landingPage
                          : '-'
                      }
                    />
                  </div>
                  <div className="overflow-hidden">
                    <LabelWithVal keyName="Route Type ID" val={promotionReviewData.routeTypeId || '-'} />
                  </div>
                </div>
              </div>
            </div>

            {/* Dynamic Language Content */}
            {languages.map((lang, index) => {
              const languageName = lang.language
              const language = lang.code

              return (
                <div key={language} className="mt-4 w-full overflow-hidden">
                  <div className="language-wrapper pt-5">
                    <span className="">Language:</span> <span className="font-bold">{languageName || ''}</span>
                  </div>
                  <div
                    className={`${index !== languages.length - 1 ? 'sc-border' : ''} grid w-full grid-cols-3 gap-6 overflow-hidden pb-5`}
                  >
                    <LabelWithVal keyName="Product Tag" val={promotionReviewData.text?.[language]?.productTag || '-'} />
                    <LabelWithVal keyName="Banner Tag" val={promotionReviewData.text?.[language]?.bannerTag || '-'} />
                    <LabelWithVal keyName="Excerpt" val={promotionReviewData.text?.[language]?.excerpt || '-'} />
                    <LabelWithVal keyName="Title" val={promotionReviewData.text?.[language]?.title || '-'} />
                    <LabelWithVal keyName="Content" val={promotionReviewData.text?.[language]?.content || '-'} />
                    <LabelWithVal keyName="Image Text" val={promotionReviewData.text?.[language]?.bannerText || '-'} />
                    <LabelWithVal
                      keyName="Description"
                      val={promotionReviewData.text?.[language]?.description || '-'}
                    />
                  </div>
                </div>
              )
            })}
          </div>
        }
      />

      {/* Image Preview Modal */}
      {showImagePreview && (
        <Modal
          showModal={showImagePreview}
          onCloseModal={handleClosePreview}
          title="Preview of Promo"
          isDark={true}
          showCloseButton={true}
          className="image-preview-modal"
          content={
            <div className="relative mx-auto w-full max-w-2xl  pt-12">
              <div
                className="relative overflow-hidden rounded-lg border border-dotted border-[#818181] "
                style={{width: '328px', height: '246px'}}
              >
                <img
                  src={promotionReviewData?.bannerUrl || ''}
                  alt="Product Promotion Preview"
                  className="h-full w-full object-cover"
                />
                {/* Promotion text overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h2 className="text-2xl font-bold text-white">
                    {promotionReviewData?.promotionName || promotionReviewData?.text?.en?.title || 'Promotion Title'}
                  </h2>
                </div>
              </div>
            </div>
          }
        />
      )}
    </>
  )
}

export default PromotionsReviewDisplay
